import { Helmet } from 'react-helmet-async';
import { filter, toLower, includes } from 'lodash';
import { useEffect, useRef, useState } from 'react';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
// components
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
// sections
import { UserListToolbar } from '../sections/@dashboard/user';
// mock
import { IMAGE_BASE_URL } from '../redux/api/http-common';
import OrderProductListHead from '../sections/@dashboard/user/OrderProductListHead';
import { getFreelanceJobMethod } from '../redux/slice/freelanceJobSlice';
import AddFreelanceJobDialog from '../components/Dialog/AddFormDialog/AddFreelanceJobDialog';
import FreelanceJobService from '../redux/api/FreelanceJobService';
import UpdateFreelanceJobDialog from '../components/Dialog/UpdateFormDialog/UpdateFreelanceJobDialog';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'jobTitle', label: ' Title', alignRight: false },
  { id: 'priceBeforeAssignJob', label: 'Price', alignRight: false },
  { id: 'experienceLevel', label: 'Experience Level', alignRight: false },
  { id: 'jobDescription', label: 'Description', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'jobAttachments', label: 'Attachments', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function FreelanceJobPage() {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [productId, setProductId] = useState(null);

  const [open, setOpen] = useState(null);

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('asc');

  const [orderBy, setOrderBy] = useState('name');

  const [filterName, setFilterName] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openAddForm, setOpenAddForm] = useState(false);

  const [openEditForm, setOpenEditForm] = useState(false);

  const data = useSelector((state) => state.freelanceJobadmin);
  const userData = useSelector((state) => state.user.userInfo);

  function applySortFilter(array, comparator, query) {
    const stabilizedThis = Array.isArray(array) && array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    if (query) {
      return filter(array, (_user) => {
        const lowerQuery = toLower(query);
        return (
          includes(toLower(!_user?.jobTitle ? '' : _user?.jobTitle), lowerQuery) ||
          includes(toLower(!_user?.experienceLevel ? '' : _user?.experienceLevel), lowerQuery)
        );
      });
    }
    return stabilizedThis.map((el) => el[0]);
  }

  useEffect(() => {
    dispatch(getFreelanceJobMethod());
  }, [dispatch]);

  const handleClickOpenAddForm = () => {
    setOpenAddForm(!openAddForm);
    handleCloseMenu();
  };

  const handleClickOpenEditForm = () => {
    setOpenEditForm(!openEditForm);
    handleCloseMenu();
  };

  const handleOpenMenu = (event, item) => {
    setProductId(item._id);
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const filteredUsers = applySortFilter(
    data.data !== null && data.data !== undefined ? data.data : [],
    getComparator(order, orderBy),
    filterName
  );
  const emptyRows = rowsPerPage - Math.min(rowsPerPage, filteredUsers.length - page * rowsPerPage);

  const isNotFound = !filteredUsers.length && !!filterName;

  const dialogRef = useRef(null);

  // Function to scroll to the top of the dialog content
  const scrollToTop = () => {
    if (dialogRef.current) {
      dialogRef.current.scrollTo(0, 0);
    }
  };

  // Use the useEffect hook to scroll to the top when the component is mounted
  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <>
      <Helmet>
        <title>Freelance Jobs | PakPrint Wishes </title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Freelance Jobs
          </Typography>
          <Button sx={{ margin: '5px' }} variant="contained" onClick={handleClickOpenAddForm}>
            Add Freelance Job
          </Button>
        </Stack>
        <Card>
          <UserListToolbar numSelected={0} filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <OrderProductListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { _id } = row;
                    return (
                      <TableRow hover key={_id}>
                        <TableCell component="th" scope="row">
                          <Typography variant="subtitle2" noWrap>
                            {!row.jobTitle ? 'N/A' : row.jobTitle}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">{!row.priceBeforeAssignJob ? '0' : row.priceBeforeAssignJob}</TableCell>
                        <TableCell align="left">{!row.experienceLevel ? 'N/A' : row.experienceLevel}</TableCell>
                        <TableCell align="left" sx={{fontSize:'10px'}}>
                          {!row.jobDescription ? (
                            'N/A'
                          ) : (
                            <div dangerouslySetInnerHTML={{ __html: row.jobDescription.slice(0, 100) }} />
                          )}
                        </TableCell>
                        <TableCell align="left">
                          {!row.jobAssigned || !row?.jobAssigned?.assigned ? 'Not Assign' : 'Assigned'}
                        </TableCell>
                        <TableCell align="left">
                          {!row.jobAttachments
                            ? 'N/A'
                            : row.jobAttachments.map((item, index) => (
                                <a
                                  href={IMAGE_BASE_URL + item}
                                  key={index}
                                  target="_blank"
                                  style={{ textDecoration: 'none', color: 'red' }}
                                  rel="noreferrer"
                                >
                                  Attachment {index}
                                </a>
                              ))}
                        </TableCell>
                        <TableCell align="right" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <IconButton
                            size="large"
                            color="inherit"
                            onClick={(e) => {
                              handleOpenMenu(e, row);
                            }}
                          >
                            <Iconify icon={'eva:more-vertical-fill'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Not found
                          </Typography>

                          <Typography variant="body2">
                            No results found for &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            navigate(`/dashboard/custom-product/custom-product-detail/${productId}`);
          }}
        >
          <Iconify icon={'carbon:view'} sx={{ mr: 2 }} />
          Proposals
        </MenuItem>
        <MenuItem onClick={handleClickOpenEditForm}>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>
        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={async () => {
            await FreelanceJobService.deletes(productId)
              .then((res) => {
                console.warn('--------', res);
                toast.success(res?.data?.message);
                dispatch(getFreelanceJobMethod());
                setProductId(null);
                setOpen(false);
              })
              .catch((error) => {
                if (!error.response.data.message) {
                  toast.error(error.message);
                } else {
                  toast.error(error.response.data.message);
                }
              });
          }}
        >
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>
      {openAddForm && (
        <AddFreelanceJobDialog openAddForm={openAddForm} handleClickOpenAddForm={handleClickOpenAddForm} />
      )}

      <UpdateFreelanceJobDialog
        openEditForm={openEditForm}
        handleClickOpenEditForm={handleClickOpenEditForm}
        jobId={productId}
      />
    </>
  );
}
